import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRouter from './routes/AppRouter';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationCenterProvider } from './contexts/NotificationCenterContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { logErrorToService } from './utils/logService';
import { listenForegroundMessages, reconcileCurrentNotificationRegistration } from './services/notificationService';
import {
  buildMunicipioLoginUrl,
  persistMunicipioId,
  resolveMunicipioContext,
  shouldCanonicalizeLegacyShortcut,
  updateMunicipalManifestLink,
} from './utils/municipioContext';

const SystemUpdateNotice = React.lazy(() => import('./components/common/SystemUpdateNotice'));

const isOpaqueCrossOriginScriptError = (event) => (
  event?.message === 'Script error.' &&
  !event?.error &&
  !event?.filename &&
  !event?.lineno &&
  !event?.colno
);


const isKnownInjectedBrowserError = (event) => {
  const message = `${event?.message || ''} ${event?.error?.message || ''}`.toLowerCase();
  const filename = String(event?.filename || '').toLowerCase();
  const extensionSource = ['moz-extension://', 'chrome-extension://', 'safari-web-extension://']
    .some((prefix) => filename.startsWith(prefix));

  return extensionSource
    || message.includes('__firefox__')
    || message.includes('window.ethereum.selectedaddress')
    || message.includes('ethereum.selectedaddress')
    || message.includes('darkreader');
};

if (typeof window !== 'undefined') {
  window.deferredPrompt = window.deferredPrompt || null;

  if (!window.__sisfolServidorInstallPromptRegistered) {
    window.__sisfolServidorInstallPromptRegistered = true;

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      window.deferredPrompt = event;
    });
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logErrorToService(
      'error',
      'portal-servidor',
      error,
      this.props.user,
      'REACT_ERROR',
      errorInfo?.componentStack || null,
      { category: 'react', severity: 'high' }
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950 p-4">
          <div className="max-w-lg w-full bg-white dark:bg-gray-900 rounded-2xl border border-red-100 dark:border-red-900 p-6 shadow-xl text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-50 dark:bg-red-950 flex items-center justify-center">
              <span className="text-red-600 dark:text-red-300 text-xl font-black">!</span>
            </div>

            <h1 className="text-xl font-black text-gray-900 dark:text-white">
              Não foi possível carregar esta tela
            </h1>

            <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              Atualize a página e tente novamente. Se continuar acontecendo, procure o Departamento Pessoal.
            </p>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white hover:bg-blue-800"
            >
              Atualizar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent = () => {
  const { user, logout, authReady } = useAuth();

  React.useEffect(() => {
    const handleGlobalError = (event) => {
      if (isOpaqueCrossOriginScriptError(event) || isKnownInjectedBrowserError(event)) return;
      const filename = String(event?.filename || '').split(/[\\/]/).pop() || 'nao-informado';
      logErrorToService('error', 'portal-servidor', event.error || new Error(event.message || 'Erro global desconhecido'), user, 'JS_ERROR', null, {
        source: 'window-error',
        context: `window-error file=${filename} line=${Number(event?.lineno) || 0} column=${Number(event?.colno) || 0}`,
      });
    };
    const handlePromiseRejection = (event) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'Promise rejeitada sem detalhes'));
      const reasonType = event.reason?.constructor?.name || typeof event.reason || 'nao-informado';
      logErrorToService('error', 'portal-servidor', error, user, 'PROMISE_ERROR', null, {
        source: 'unhandledrejection',
        context: `promise-rejection reasonType=${reasonType}`,
      });
    };
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, [user]);

  React.useEffect(() => {
    if (!authReady || !user?.uid) return undefined;
    let unsubscribe = () => {};
    let active = true;
    listenForegroundMessages().then((listener) => {
      if (active) unsubscribe = listener;
      else listener();
    }).catch((error) => {
      console.warn('Não foi possível iniciar as notificações em primeiro plano:', error);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [authReady, user?.uid]);

  React.useEffect(() => {
    if (!authReady || !user?.uid || !user?.municipioId) return undefined;

    let disposed = false;
    let lastVisibilityCheck = 0;
    const reconcile = async (reason) => {
      try {
        await reconcileCurrentNotificationRegistration({
          municipioId: user.municipioId,
          reason,
        });
      } catch (error) {
        if (!disposed) {
          console.warn('Não foi possível reconciliar o registro de notificações deste aparelho:', error);
        }
      }
    };

    reconcile('auth-ready');

    const handleServiceWorkerUpdate = (event) => {
      const waitingWorker = event?.detail?.registration?.waiting;
      if (waitingWorker) {
        waitingWorker.postMessage({type: 'SISFOL_ACTIVATE_SERVICE_WORKER'});
      }
    };

    const handleControllerChange = () => {
      reconcileCurrentNotificationRegistration({
        municipioId: user.municipioId,
        forceRefresh: true,
        reason: 'service-worker-update',
      }).catch((error) => {
        if (!disposed) console.warn('Falha ao reconciliar push após atualização do aplicativo:', error);
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityCheck < 6 * 60 * 60 * 1000) return;
      lastVisibilityCheck = now;
      reconcile('visibility-resume');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('sisfol:service-worker-update', handleServiceWorkerUpdate);
    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('sisfol:service-worker-update', handleServiceWorkerUpdate);
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [authReady, user?.uid, user?.municipioId]);

  React.useEffect(() => {
    const context = resolveMunicipioContext({ sessionMunicipioId: user?.municipioId });
    if (context.source === 'invalid-url' && user?.municipioId) {
      logout('O link municipal é inválido. Abra novamente o link oficial enviado pelo Departamento Pessoal.');
      return;
    }
    if (!context.conflict) return;

    logout('O link aberto pertence a outro município. Entre novamente para continuar com segurança.');
  }, [logout, user?.municipioId]);

  return (
    <ErrorBoundary user={user}>
      <AppRouter />
      {authReady && user && (
        <React.Suspense fallback={null}>
          <SystemUpdateNotice enabled portalKey="servidor" portalLabel="Portal do Servidor" />
        </React.Suspense>
      )}
    </ErrorBoundary>
  );
};

function App() {
  const initialContext = React.useMemo(() => resolveMunicipioContext(), []);

  React.useEffect(() => {
    const { municipioId, source } = initialContext;
    updateMunicipalManifestLink(municipioId);

    if (municipioId) {
      persistMunicipioId(municipioId);
    }

    if (shouldCanonicalizeLegacyShortcut({ source })) {
      const canonicalUrl = buildMunicipioLoginUrl(municipioId, { source: 'legacy-shortcut' });
      if (canonicalUrl) window.history.replaceState({}, '', canonicalUrl);
    }
  }, [initialContext]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <NotificationCenterProvider>
            <Router future={{ v7_startTransition: false, v7_relativeSplatPath: true }}>
              <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
                <AppContent />
              </div>
            </Router>
          </NotificationCenterProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
